import {
  useMemo, useCallback, useState, useRef
} from 'react'
import {Box, Notification} from '@binance/uikit'
import useOrderForm from 'hooks/useOrderForm'
import useRouter from 'hooks/useRouter'
import useSymbolInfo from 'hooks/useSymbolInfo'
import useUserLoginStatus from 'hooks/useUserLoginStatus'
import {useDispatch} from 'redux-react-hook'
import {
  updateOrderPrice,
  updateOrderQty,
  updateOrderStopPrice,
  resetOrderForm,
  updateOrderTradeStatus,
  updateLimitType,
  updateTimeInForce
} from 'actions'
import {
  TRADE_TYPES, TRADE_SIDES, LIMIT_TYPES, TIME_IN_FORCE
} from 'constants'
import {floor} from 'utils/number'
import RequireLoginEl from './RequireLoginEl'
import StopLimitForm from './StopLimitForm'
import ConfirmDialog from './ConfirmDialog'
import TradeLimitType from './TradeLimitType'

export default function StopFormContainer ({i18n}) {
  const lastTifType = useRef(TIME_IN_FORCE.TIF.GTC)
  const [side, setSide] = useState(TRADE_SIDES.BUY)
  const [showDialog, setShowDialog] = useState(false)
  const dispatch = useDispatch()
  const formType = TRADE_TYPES.STOP

  const isLogin = useUserLoginStatus()
  const router = useRouter()
  const {symbol} = router.query
  const {symbolInfo} = useSymbolInfo()

  const {
    baseAsset, quoteAsset, pricePrecision, quantityPrecision, maxLen
  } = symbolInfo

  const {
    handleBuy,
    handleSell,
    availableBalance,
    orderBuyValue,
    marginBuyRequired,
    orderSellValue,
    marginSellRequired,
    maxPrice,
    limitMaxQty,
    buyStopPrice,
    buyPrice,
    buyQty,
    sellStopPrice,
    sellPrice,
    sellQty,
    buyStatus,
    sellStatus,
    limitType,
    maxBuyQty,
    maxSellQty,
    reduceOnly,
    positionSize
  } = useOrderForm(symbol, quoteAsset, formType)

  const reset = useCallback(
    buyOrSell => {
      dispatch(resetOrderForm(buyOrSell, formType))
    },
    [dispatch, formType]
  )

  const setTypeInForce = useCallback(
    (type, isNotTif) => {
      if (!isNotTif) lastTifType.current = type
      dispatch(updateTimeInForce(formType, type))
    },
    [dispatch, formType]
  )

  const setLimitType = useCallback(
    type => {
      dispatch(updateLimitType(formType, type))
      const isTifType = type === LIMIT_TYPES.TIF
      const resetLimitType = isTifType ? lastTifType.current : TIME_IN_FORCE.POST_ONLY
      setTypeInForce(resetLimitType, !isTifType)
    },
    [dispatch, formType, setTypeInForce]
  )

  const switchTradeStatus = useCallback(
    (buyOrSell, status) => {
      dispatch(updateOrderTradeStatus(buyOrSell, status, formType))
    },
    [dispatch, formType]
  )

  const switchDialog = useCallback(() => {
    setShowDialog(!showDialog)
  }, [showDialog])

  const buyHandler = useCallback(
    isConfirmed => {
      const bPrice = Number(buyPrice)
      const bQty = Number(buyQty)
      const bStopPrice = Number(buyStopPrice)
      if (bPrice > 0 && bQty > 0 && bStopPrice > 0) {
        const tradeSide = TRADE_SIDES.BUY
        setSide(tradeSide)
        const isTrade = isConfirmed === true
          || JSON.parse(window.localStorage.getItem(`dialog_${formType}_${tradeSide}`))
        if (isTrade) {
          reset(tradeSide)
          switchTradeStatus(tradeSide, true)
          handleBuy().then(d => {
            switchTradeStatus(tradeSide, false)
            if (d?.order) {
              Notification({
                content: i18n['stopLimitForm-buySuccess'],
                type: 'success',
                duration: 3000
                // time: formatDate(new Date(), 'HH:mm:ss')
              })
            } else {
              Notification({
                content: d?.error?.message || i18n['stopLimitForm-buyFailure'],
                type: 'error',
                duration: 3000
                // time: formatDate(new Date(), 'HH:mm:ss')
              })
            }
          })
        } else {
          switchDialog()
        }
      }
    },
    [buyPrice, buyQty, buyStopPrice, formType, handleBuy, i18n, reset, switchDialog, switchTradeStatus]
  )

  const sellHandler = useCallback(
    isConfirmed => {
      const sPrice = Number(sellPrice)
      const sQty = Number(sellQty)
      const sStopPrice = Number(sellStopPrice)
      if (sPrice > 0 && sQty > 0 && sStopPrice > 0) {
        const tradeSide = TRADE_SIDES.SELL
        setSide(tradeSide)
        const isTrade = isConfirmed === true
          || JSON.parse(window.localStorage.getItem(`dialog_${formType}_${tradeSide}`))
        if (isTrade) {
          reset(tradeSide)
          switchTradeStatus(tradeSide, true)
          handleSell().then(d => {
            switchTradeStatus(tradeSide, false)
            if (d?.order) {
              Notification({
                content: i18n['stopLimitForm-sellSuccess'],
                type: 'success',
                duration: 3000
                // time: formatDate(new Date(), 'HH:mm:ss')
              })
            } else {
              Notification({
                content: d?.error?.message || i18n['stopLimitForm-sellFailure'],
                type: 'error',
                duration: 3000
                // time: formatDate(new Date(), 'HH:mm:ss')
              })
            }
          })
        } else {
          switchDialog()
        }
      }
    },
    [formType, handleSell, i18n, reset, sellPrice, sellQty, sellStopPrice, switchDialog, switchTradeStatus]
  )

  const getBuySliderAvailableRange = useCallback(() => {
    let availableRange = {min: 0, max: 100}
    const pSize = Number(positionSize)
    if (isLogin) {
      if (reduceOnly) {
        if (pSize) {
          pSize > 0 && (availableRange = {min: 0, max: 0})
        } else {
          availableRange = {min: 0, max: 0}
        }
      }
      if (maxBuyQty === 0) {
        availableRange = {min: 0, max: 0}
      }
    } else {
      availableRange = {min: 0, max: 0}
    }
    return availableRange
  }, [isLogin, positionSize, reduceOnly, maxBuyQty])

  const getSellSliderAvailableRange = useCallback(() => {
    let availableRange = {min: 0, max: 100}
    const pSize = Number(positionSize)
    if (isLogin) {
      if (reduceOnly) {
        if (pSize) {
          pSize < 0 && (availableRange = {min: 0, max: 0})
        } else {
          availableRange = {min: 0, max: 0}
        }
        if (maxSellQty === 0) {
          availableRange = {min: 0, max: 0}
        }
      }
    } else {
      availableRange = {min: 0, max: 0}
    }
    return availableRange
  }, [isLogin, positionSize, reduceOnly, maxSellQty])

  const changeSliderValue = useCallback(({val, type}) => {
    const maxQty = type === 'buy' ? maxBuyQty : maxSellQty
    val = floor(maxQty * val / 100, quantityPrecision)
    dispatch(updateOrderQty(type, val, formType))
  }, [maxBuyQty, maxSellQty, quantityPrecision, dispatch, formType])

  return useMemo(
    () => (
      <>
        {showDialog && (
          <ConfirmDialog
            i18n={i18n}
            visible={showDialog}
            type={formType}
            side={side}
            onClose={switchDialog}
            handleBuy={buyHandler}
            handleSell={sellHandler}
          />
        )}
        <Box mx={5} my={3}>
          <TradeLimitType
            name="stopRadio"
            i18n={i18n}
            limitType={limitType}
            setLimitType={setLimitType}
            setTypeInForce={setTypeInForce}
            availableBalance={availableBalance}
          />
        </Box>
        <StopLimitForm
          i18n={i18n}
          isLogin={isLogin}
          requireLoginEl={<RequireLoginEl i18n={i18n} />}
          baseAsset={baseAsset}
          quoteAsset={quoteAsset}
          buyStopPrice={buyStopPrice}
          pricePrecision={pricePrecision}
          quantityPrecision={quantityPrecision}
          maxLen={maxLen}
          maxPrice={maxPrice}
          limitMaxQty={limitMaxQty}
          buyPrice={buyPrice}
          buyQty={buyQty}
          sellStopPrice={sellStopPrice}
          sellPrice={sellPrice}
          sellQty={sellQty}
          orderBuyValue={orderBuyValue}
          orderSellValue={orderSellValue}
          marginBuyRequired={marginBuyRequired}
          marginSellRequired={marginSellRequired}
          handleStopPriceChange={(buyOrSell, stopPrice) => {
            dispatch(updateOrderStopPrice(buyOrSell, stopPrice, formType))
          }}
          handlePriceChange={(buyOrSell, price) => {
            dispatch(updateOrderPrice(buyOrSell, price, formType))
          }}
          handleQtyChange={(buyOrSell, qty) => {
            dispatch(updateOrderQty(buyOrSell, qty, formType))
          }}
          handleBuy={buyHandler}
          handleSell={sellHandler}
          buyStatus={buyStatus}
          sellStatus={sellStatus}
          reduceOnly={reduceOnly}
          buyAvailableRange={getBuySliderAvailableRange()}
          sellAvailableRange={getSellSliderAvailableRange()}
          changeSliderValue={changeSliderValue}
        />
      </>
    ),
    [showDialog, i18n, formType, side, switchDialog, buyHandler, sellHandler, limitType, setLimitType, setTypeInForce, availableBalance, isLogin, baseAsset, quoteAsset, buyStopPrice, pricePrecision, quantityPrecision, maxLen, maxPrice, limitMaxQty, buyPrice, buyQty, sellStopPrice, sellPrice, sellQty, orderBuyValue, orderSellValue, marginBuyRequired, marginSellRequired, buyStatus, sellStatus, reduceOnly, getBuySliderAvailableRange, getSellSliderAvailableRange, changeSliderValue, dispatch]
  )
}
